import { create } from 'zustand';
import { todayISO } from '@/lib/format';
import type { Product } from '@/types/product';
import type { Slot } from '@/types/product.slot';
import type { Bundle, CouponValidation } from '@/types/coupon';
import type { PromoGroupType, SourceChannel, SaleIdentity, DocumentScanResult } from '@/types/sale';
import type { SeatItinerary } from '@/types/itinerary';

export interface CartLineAddOn {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  slotId?: string;
}

/**
 * Per-ticket ("seat") configuration. Each unit of a product line gets its own
 * slot, add-ons and visitor label — mirroring the online FE's per-seat
 * add-on/slot step (app-pos-tiket-online /addon-ticket).
 */
export interface LineSeat {
  slotId?: string;
  slotLabel?: string;
  addOns: CartLineAddOn[];
  label?: string;
  /**
   * The Smart Itinerary plan + timing config the cashier applied to this seat's
   * wahana (Atur Jadwal Pintar). Rides onto the matching `/sale` item so it is
   * stamped on the minted wristband's `qrCode.itinerary`. No pricing impact.
   */
  itinerary?: SeatItinerary;
}

export interface CartLine {
  /** Stable client key — the productId (one line per product; slots live per-seat). */
  key: string;
  productId: string;
  name: string;
  /** Server-resolved unit price (display estimate; reconciled by BE on sale/quote). */
  unitPrice: number;
  category?: string;
  /** Whether each seat must pick a time slot before checkout. */
  requiresSlot: boolean;
  /** Whether seats can attach add-ons. */
  hasAddOns: boolean;
  /** One entry per ticket; length === quantity. */
  seats: LineSeat[];
}

/** A bundle in the cart. Bundle composition/pricing is reconciled server-side;
 *  for FLEXIBLE bundles we carry the customer's picks in `selectedItems`. */
export interface BundleLine {
  /** Stable client key — the bundleId. */
  key: string;
  bundleId: string;
  name: string;
  unitPrice: number;
  quantity: number;
  bundleType: string;
  /** Number of component tickets (display only). */
  itemCount: number;
  /** FLEXIBLE bundles only. */
  selectedItems?: Array<{ productId: string; quantity: number; slotId?: string }>;
  /**
   * Slot-required components + the cashier's slot pick for each. The backend
   * requires a slot for every `requiresTimeSlot` component (sent as
   * `componentSlots` on the sale); a bundle with any unpicked component can't
   * be sold. Empty when the bundle has no slot-required components.
   */
  components: Array<{ productId: string; name: string; slotId?: string }>;
  /**
   * Extra attraction tickets the cashier attaches ON TOP of the bundle's fixed
   * components — each charged at its own rate (sent as bundleItems[].addOnItems
   * with bundleQuantity:0). Each is a distinct quantity-1 entry with its own
   * slot, mirroring the per-seat attraction children on individual tickets.
   */
  extraAddOns: CartLineAddOn[];
}

interface CartState {
  lines: CartLine[];
  bundleLines: BundleLine[];
  customer: { name: string; phone: string; memberId?: string };
  visitDate: string;
  couponCode: string | null;
  couponResult: CouponValidation | null;
  discountIds: string[];
  /**
   * Unified promo-scheme context (v.1.0.22). Cashier-set signals that narrow
   * which promos apply: group segment, sales channel, KTP attestation. All
   * optional; a plain sale leaves them null.
   */
  promo: {
    groupType: PromoGroupType | null;
    sourceChannel: SourceChannel | null;
    identity: SaleIdentity | null;
    /**
     * Document-OCR scan result (v.1.0.24). `documentScan` is the display
     * snapshot (extracted fields for cashier confirmation + the eligible-preview
     * context); `documentScanToken` is the single-use token the sale carries so
     * the server re-reads the trusted extraction and gates document-OCR promos.
     */
    documentScan: DocumentScanResult | null;
    documentScanToken: string | null;
  };

  addProduct: (product: Product, opts?: { quantity?: number; unitPrice?: number }) => void;
  addBundle: (
    bundle: Bundle,
    opts?: { quantity?: number; unitPrice?: number; selectedItems?: BundleLine['selectedItems'] },
  ) => void;
  /**
   * Re-price every cart line, seat add-on, bundle, and bundle extra from
   * date-resolved price maps (keyed by product / bundle id). Cart prices are
   * display-only — the BE re-resolves authoritatively at sale time — so this
   * just keeps the shown subtotal in sync with the eventual charge (e.g. after
   * the cashier changes the visit date). Ids absent from a map keep their
   * current price.
   */
  repriceCart: (
    productPrices: Record<string, number>,
    bundlePrices: Record<string, { price: number }>,
  ) => void;
  updateBundleQty: (key: string, quantity: number) => void;
  removeBundle: (key: string) => void;
  /** Set the slot pick for one slot-required bundle component. */
  setBundleComponentSlot: (key: string, productId: string, slot: Slot | undefined) => void;
  /** Replace the extra attraction add-ons attached to a bundle. */
  setBundleAddOns: (key: string, addOns: CartLineAddOn[]) => void;
  /** Resize a line's seat list (grow with blank seats, shrink from the end). */
  updateQty: (key: string, quantity: number) => void;
  removeLine: (key: string) => void;

  // Per-seat configuration (Add-ons & Slots step).
  setSeatSlot: (key: string, seatIndex: number, slot: Slot | undefined) => void;
  setSeatAddOns: (key: string, seatIndex: number, addOns: CartLineAddOn[]) => void;
  setSeatLabel: (key: string, seatIndex: number, label: string) => void;
  /** Persist the applied Smart Itinerary (plan + config) for one seat. */
  setSeatItinerary: (key: string, seatIndex: number, itinerary: SeatItinerary) => void;
  /** Copy one seat's slot + add-ons to every seat in the line (Terapkan ke semua). */
  applySeatToAll: (key: string, seatIndex: number) => void;

  setUnitPrice: (key: string, unitPrice: number) => void;
  setCustomer: (customer: Partial<CartState['customer']>) => void;
  setVisitDate: (date: string) => void;
  applyCoupon: (result: CouponValidation | null) => void;
  toggleDiscount: (id: string) => void;
  setPromo: (patch: Partial<CartState['promo']>) => void;
  clearCart: () => void;
}

const EMPTY_PROMO: CartState['promo'] = {
  groupType: null,
  sourceChannel: null,
  identity: null,
  documentScan: null,
  documentScanToken: null,
};

const EMPTY_CUSTOMER = { name: '', phone: '', memberId: undefined };

const blankSeat = (): LineSeat => ({ addOns: [] });

/** Grow/shrink a seat list to `quantity`, preserving existing seat config. */
function resizeSeats(seats: LineSeat[], quantity: number): LineSeat[] {
  if (quantity <= seats.length) return seats.slice(0, Math.max(0, quantity));
  return [...seats, ...Array.from({ length: quantity - seats.length }, blankSeat)];
}

function seatAddOnTotal(seat: LineSeat): number {
  return seat.addOns.reduce((s, a) => s + a.price * a.quantity, 0);
}

function lineSubtotal(line: CartLine): number {
  return line.seats.reduce((sum, seat) => sum + line.unitPrice + seatAddOnTotal(seat), 0);
}

function bundleLineSubtotal(line: BundleLine): number {
  const extras = (line.extraAddOns ?? []).reduce((s, a) => s + a.price * a.quantity, 0);
  return line.unitPrice * line.quantity + extras;
}

/** Map a seat config within a specific line, returning a new lines array. */
function mapSeat(
  lines: CartLine[],
  key: string,
  seatIndex: number,
  fn: (seat: LineSeat) => LineSeat,
): CartLine[] {
  return lines.map((l) =>
    l.key === key
      ? { ...l, seats: l.seats.map((seat, i) => (i === seatIndex ? fn(seat) : seat)) }
      : l,
  );
}

export const useCartStore = create<CartState>()((set) => ({
  lines: [],
  bundleLines: [],
  customer: { ...EMPTY_CUSTOMER },
  visitDate: todayISO(),
  couponCode: null,
  couponResult: null,
  discountIds: [],
  promo: { ...EMPTY_PROMO },

  addProduct: (product, opts) =>
    set((state) => {
      const qty = opts?.quantity ?? 1;
      const key = product.id;
      const existing = state.lines.find((l) => l.key === key);
      // Prefer the date-resolved price supplied by the caller; fall back to the
      // product's discounted/base catalog price.
      const unitPrice =
        typeof opts?.unitPrice === 'number'
          ? opts.unitPrice
          : product.isDiscountable && typeof product.discountedPrice === 'number'
            ? product.discountedPrice
            : product.price;

      if (existing) {
        return {
          lines: state.lines.map((l) =>
            l.key === key ? { ...l, seats: resizeSeats(l.seats, l.seats.length + qty) } : l,
          ),
        };
      }

      const newLine: CartLine = {
        key,
        productId: product.id,
        name: product.name,
        unitPrice,
        category: product.category,
        requiresSlot: Boolean(product.requiresSlot),
        hasAddOns: Boolean(product.hasAddOns),
        seats: Array.from({ length: qty }, blankSeat),
      };
      return { lines: [...state.lines, newLine] };
    }),

  addBundle: (bundle, opts) =>
    set((state) => {
      const qty = opts?.quantity ?? 1;
      const existing = state.bundleLines.find((b) => b.key === bundle.id);
      if (existing) {
        return {
          bundleLines: state.bundleLines.map((b) =>
            b.key === bundle.id ? { ...b, quantity: b.quantity + qty } : b,
          ),
        };
      }
      const newLine: BundleLine = {
        key: bundle.id,
        bundleId: bundle.id,
        name: bundle.name,
        unitPrice: typeof opts?.unitPrice === 'number' ? opts.unitPrice : bundle.price,
        quantity: qty,
        bundleType: bundle.bundleType,
        itemCount: bundle.items.reduce((s, it) => s + (it.quantity ?? 1), 0),
        selectedItems: opts?.selectedItems,
        // Each slot-required component needs its own time pick before checkout.
        components: bundle.items
          .filter((it) => it.requiresTimeSlot)
          .map((it) => ({ productId: it.productId, name: it.name ?? 'Komponen', slotId: undefined })),
        extraAddOns: [],
      };
      return { bundleLines: [...state.bundleLines, newLine] };
    }),

  updateBundleQty: (key, quantity) =>
    set((state) => ({
      bundleLines:
        quantity <= 0
          ? state.bundleLines.filter((b) => b.key !== key)
          : state.bundleLines.map((b) => (b.key === key ? { ...b, quantity } : b)),
    })),

  removeBundle: (key) =>
    set((state) => ({ bundleLines: state.bundleLines.filter((b) => b.key !== key) })),

  setBundleComponentSlot: (key, productId, slot) =>
    set((state) => ({
      bundleLines: state.bundleLines.map((b) =>
        b.key === key
          ? {
              ...b,
              components: b.components.map((c) =>
                c.productId === productId ? { ...c, slotId: slot?.id } : c,
              ),
            }
          : b,
      ),
    })),

  setBundleAddOns: (key, addOns) =>
    set((state) => ({
      bundleLines: state.bundleLines.map((b) =>
        b.key === key ? { ...b, extraAddOns: addOns } : b,
      ),
    })),

  repriceCart: (productPrices, bundlePrices) =>
    set((state) => {
      const priceOf = (productId: string, current: number) =>
        typeof productPrices[productId] === 'number' ? productPrices[productId] : current;

      const lines = state.lines.map((l) => ({
        ...l,
        unitPrice: priceOf(l.productId, l.unitPrice),
        seats: l.seats.map((seat) => ({
          ...seat,
          addOns: seat.addOns.map((a) => ({ ...a, price: priceOf(a.productId, a.price) })),
        })),
      }));

      const bundleLines = state.bundleLines.map((b) => {
        const bp = bundlePrices[b.bundleId];
        return {
          ...b,
          unitPrice: typeof bp?.price === 'number' ? bp.price : b.unitPrice,
          extraAddOns: b.extraAddOns.map((a) => ({ ...a, price: priceOf(a.productId, a.price) })),
        };
      });

      return { lines, bundleLines };
    }),

  updateQty: (key, quantity) =>
    set((state) => ({
      lines:
        quantity <= 0
          ? state.lines.filter((l) => l.key !== key)
          : state.lines.map((l) => (l.key === key ? { ...l, seats: resizeSeats(l.seats, quantity) } : l)),
    })),

  removeLine: (key) =>
    set((state) => ({ lines: state.lines.filter((l) => l.key !== key) })),

  setSeatSlot: (key, seatIndex, slot) =>
    set((state) => ({
      lines: mapSeat(state.lines, key, seatIndex, (seat) => ({
        ...seat,
        slotId: slot?.id,
        slotLabel: slot?.localStart,
      })),
    })),

  setSeatAddOns: (key, seatIndex, addOns) =>
    set((state) => ({
      lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, addOns })),
    })),

  setSeatLabel: (key, seatIndex, label) =>
    set((state) => ({
      lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, label })),
    })),

  setSeatItinerary: (key, seatIndex, itinerary) =>
    set((state) => ({
      lines: mapSeat(state.lines, key, seatIndex, (seat) => ({ ...seat, itinerary })),
    })),

  applySeatToAll: (key, seatIndex) =>
    set((state) => ({
      lines: state.lines.map((l) => {
        if (l.key !== key) return l;
        const src = l.seats[seatIndex];
        if (!src) return l;
        return {
          ...l,
          seats: l.seats.map((seat) => ({
            ...seat,
            slotId: src.slotId,
            slotLabel: src.slotLabel,
            addOns: src.addOns.map((a) => ({ ...a })),
          })),
        };
      }),
    })),

  setUnitPrice: (key, unitPrice) =>
    set((state) => ({
      lines: state.lines.map((l) => (l.key === key ? { ...l, unitPrice } : l)),
    })),

  setCustomer: (customer) =>
    set((state) => ({ customer: { ...state.customer, ...customer } })),

  setVisitDate: (date) => set({ visitDate: date }),

  applyCoupon: (result) =>
    set({ couponResult: result, couponCode: result?.valid ? result.code : null }),

  toggleDiscount: (id) =>
    set((state) => ({
      discountIds: state.discountIds.includes(id)
        ? state.discountIds.filter((d) => d !== id)
        : [...state.discountIds, id],
    })),

  setPromo: (patch) =>
    set((state) => ({ promo: { ...state.promo, ...patch } })),

  clearCart: () =>
    set({
      lines: [],
      bundleLines: [],
      customer: { ...EMPTY_CUSTOMER },
      couponCode: null,
      couponResult: null,
      discountIds: [],
      promo: { ...EMPTY_PROMO },
    }),
}));

// ─── Selectors (subscribe narrowly) ────────────────────────────────────────
export const cartSelectors = {
  subtotal: (s: CartState): number =>
    s.lines.reduce((sum, l) => sum + lineSubtotal(l), 0) +
    s.bundleLines.reduce((sum, b) => sum + bundleLineSubtotal(b), 0),
  itemCount: (s: CartState): number =>
    s.lines.reduce((sum, l) => sum + l.seats.length, 0) +
    s.bundleLines.reduce((sum, b) => sum + b.quantity, 0),
  /** Total distinct lines (products + bundles) — for empty-cart guards. */
  lineCount: (s: CartState): number => s.lines.length + s.bundleLines.length,
  couponSavings: (s: CartState): number =>
    s.couponResult?.valid ? (s.couponResult.discountAmount ?? 0) : 0,
  /** Line keys still needing a slot pick on any seat (Add-ons & Slots gate). */
  incompleteLineKeys: (s: CartState): string[] =>
    s.lines
      .filter((l) => l.requiresSlot && l.seats.some((seat) => !seat.slotId))
      .map((l) => l.key),
};

export function selectTotal(s: CartState): number {
  return Math.max(0, cartSelectors.subtotal(s) - cartSelectors.couponSavings(s));
}

export { lineSubtotal, bundleLineSubtotal, seatAddOnTotal };
